import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { RouteItem } from '@/types/routeMap';
import { RouteStatCard } from './RouteStatCard';
import { makeRouteColorScale } from './colorScale';

interface RouteStatListProps {
  routes: RouteItem[];
  topN?: number;
}

export function RouteStatList({ routes, topN = 5 }: RouteStatListProps) {
  const [expanded, setExpanded] = useState(false);

  const sorted = useMemo(
    () => [...routes].sort((a, b) => b.count - a.count),
    [routes],
  );

  const colorScale = useMemo(() => {
    const max = sorted.reduce((m, r) => Math.max(m, r.count), 1);
    return makeRouteColorScale(max);
  }, [sorted]);

  const visible = expanded ? sorted : sorted.slice(0, topN);
  const hasMore = sorted.length > topN;

  if (sorted.length === 0) {
    return (
      <div className="rounded-xl bg-white/5 ring-1 ring-white/10 p-6 text-center text-sm text-slate-400">
        暂无航线数据
      </div>
    );
  }

  return (
    <div>
      <motion.ul layout className="space-y-2">
        <AnimatePresence initial={false}>
          {visible.map((r) => (
            <motion.li
              key={`${r.departure}-${r.arrival}`}
              layout
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.18 }}
            >
              <RouteStatCard route={r} colorScale={colorScale} />
            </motion.li>
          ))}
        </AnimatePresence>
      </motion.ul>

      {hasMore && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs text-cyan-300/80 hover:text-cyan-300 py-2 rounded-lg ring-1 ring-cyan-400/20 hover:ring-cyan-400/40 hover:bg-cyan-400/5 transition-colors"
        >
          {expanded ? (
            <>
              收起 <ChevronUp className="w-3.5 h-3.5" />
            </>
          ) : (
            <>
              展开全部 {sorted.length} 条 <ChevronDown className="w-3.5 h-3.5" />
            </>
          )}
        </button>
      )}
    </div>
  );
}
